(() => {
  const updateDisplayedPrice = (section, current, compare = '', context = '') => {
    const price = section?.querySelector('[data-product-price]');
    if (!price) return;

    const currentValue = price.querySelector('[data-current-price-value]');
    const compareWrap = price.querySelector('[data-compare-price]');
    const compareValue = compareWrap?.querySelector('s');
    const priceLabel = price.querySelector('[data-price-label]');
    const priceContext = price.querySelector('[data-price-context]');
    const hasCompare = Boolean(compare && compare !== current);

    if (currentValue) currentValue.textContent = current || '';
    if (compareWrap) compareWrap.hidden = !hasCompare;
    if (compareValue) compareValue.textContent = hasCompare ? compare : '';
    if (priceLabel) priceLabel.textContent = hasCompare ? 'Sale price' : 'Price';
    if (priceContext) {
      priceContext.textContent = context;
      priceContext.hidden = !context;
    }
    price.classList.toggle('natural-price--sale', hasCompare);
  };

  const initializeProductForm = (root) => {
    if (root.dataset.initialized === 'true') return;

    const section = root.closest('[data-product-section]') || root.closest('.natural-product');
    const select = root.querySelector('[data-variant-select]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const availability = root.querySelector('[data-product-availability]');
    const availabilityText = root.querySelector('[data-availability-text]');
    const quantity = root.querySelector('[data-quantity-input]');
    const purchaseCards = [...root.querySelectorAll('[data-purchase-card]')];
    const sellingPlanChoices = [...root.querySelectorAll('[data-selling-plan-choice]')];
    const subscriptionBody = root.querySelector('[data-subscription-body]');
    const subscriptionPrice = root.querySelector('[data-subscription-card-price]');
    const subscriptionCompare = root.querySelector('[data-subscription-card-compare]');
    const subscriptionSaving = root.querySelector('[data-subscription-saving]');
    const oneTimeCardPrice = root.querySelector('[data-one-time-card-price]');

    const setQuantityRules = (option) => {
      if (!quantity || !option) return;
      const minimum = Number(option.dataset.quantityMin || '1');
      const increment = Number(option.dataset.quantityIncrement || '1');
      const maximum = option.dataset.quantityMax ? Number(option.dataset.quantityMax) : Number.POSITIVE_INFINITY;

      quantity.min = String(minimum);
      quantity.step = String(increment);
      if (Number.isFinite(maximum)) quantity.max = String(maximum);
      else quantity.removeAttribute('max');

      const current = Number(quantity.value);
      const safeCurrent = Number.isFinite(current) ? current : minimum;
      const stepped = minimum + Math.floor(Math.max(0, safeCurrent - minimum) / increment) * increment;
      quantity.value = String(Math.min(maximum, Math.max(minimum, stepped)));
    };

    const setPurchaseCardState = (type) => {
      purchaseCards.forEach((card) => card.classList.toggle('is-selected', card.dataset.purchaseCard === type));
      if (subscriptionBody) subscriptionBody.hidden = type !== 'subscription';
    };

    const applySubscription = (choice) => {
      if (!choice) return;
      if (subscriptionPrice) subscriptionPrice.textContent = choice.dataset.price || '';
      if (subscriptionCompare) {
        subscriptionCompare.textContent = choice.dataset.comparePrice || '';
        subscriptionCompare.hidden = !choice.dataset.comparePrice;
      }
      if (subscriptionSaving) {
        subscriptionSaving.textContent = choice.dataset.saving
          ? `${choice.dataset.saving}% savings applied.`
          : 'Membership savings applied.';
      }
      updateDisplayedPrice(section, choice.dataset.price, choice.dataset.comparePrice, 'with membership');
      setPurchaseCardState('subscription');
    };

    const applyOneTime = () => {
      updateDisplayedPrice(section, root.dataset.oneTimePrice, root.dataset.oneTimeComparePrice, '');
      setPurchaseCardState('one-time');
    };

    const applySellingPlanChoice = (choice) => {
      if (!choice) return;
      if (choice.dataset.purchaseType === 'subscription') applySubscription(choice);
      else applyOneTime();
    };

    const updateVariant = () => {
      if (!select) return;
      const option = select.options[select.selectedIndex];
      if (!option) return;

      const isAvailable = option.dataset.available === 'true';
      root.dataset.oneTimePrice = option.dataset.price || '';
      root.dataset.oneTimeComparePrice = option.dataset.comparePrice || '';
      if (oneTimeCardPrice) oneTimeCardPrice.textContent = option.dataset.price || '';

      if (addButton) {
        addButton.disabled = !isAvailable;
        addButton.textContent = isAvailable ? addButton.dataset.availableLabel : addButton.dataset.soldOutLabel;
      }
      if (availability) availability.classList.toggle('is-unavailable', !isAvailable);
      if (availabilityText) availabilityText.textContent = option.dataset.inventoryMessage || '';
      setQuantityRules(option);

      applySellingPlanChoice(sellingPlanChoices.find((input) => input.checked));

      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    };

    sellingPlanChoices.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        applySellingPlanChoice(input);
      });
    });

    select?.addEventListener('change', updateVariant);
    root.dataset.initialized = 'true';

    if (select) updateVariant();
    else applySellingPlanChoice(sellingPlanChoices.find((input) => input.checked));
  };

  const initializeGallery = (gallery) => {
    if (gallery.dataset.initialized === 'true') return;
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const thumbnails = [...gallery.querySelectorAll('[data-gallery-thumbnail]')];
    const previous = gallery.querySelector('[data-gallery-previous]');
    const next = gallery.querySelector('[data-gallery-next]');
    const status = gallery.querySelector('[data-gallery-status]');
    if (!slides.length) return;

    let activeIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-selected') === 'true'));

    const show = (index, focusThumbnail = false) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.hidden = slideIndex !== activeIndex;
        slide.classList.toggle('is-active', slideIndex === activeIndex);
      });
      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isActive = thumbnailIndex === activeIndex;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-selected', String(isActive));
        thumbnail.tabIndex = isActive ? 0 : -1;
      });
      if (status) status.textContent = `Image ${activeIndex + 1} of ${slides.length}`;
      if (focusThumbnail) thumbnails[activeIndex]?.focus();
    };

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => show(index));
      thumbnail.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        show(index + (event.key === 'ArrowRight' ? 1 : -1), true);
      });
    });
    previous?.addEventListener('click', () => show(activeIndex - 1));
    next?.addEventListener('click', () => show(activeIndex + 1));
    gallery.dataset.initialized = 'true';
    show(activeIndex);
  };

  const initializeSingleOpen = (group) => {
    if (group.dataset.singleOpenInitialized === 'true') return;
    const items = [...group.querySelectorAll(':scope > details')];
    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });
    group.dataset.singleOpenInitialized = 'true';
  };

  const initializeStickyPurchase = (section) => {
    if (section.dataset.stickyInitialized === 'true') return;
    const sticky = section.querySelector('[data-product-sticky]');
    const purchase = section.querySelector('.natural-product__info');
    if (!sticky || !purchase || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      },
      { rootMargin: '-80px 0px 0px', threshold: 0.05 }
    );
    observer.observe(purchase);
    section.dataset.stickyInitialized = 'true';
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-product-form-root]').forEach(initializeProductForm);
    scope.querySelectorAll('[data-product-gallery]').forEach(initializeGallery);
    scope.querySelectorAll('[data-single-open]').forEach(initializeSingleOpen);
    scope.querySelectorAll('[data-product-section]').forEach(initializeStickyPurchase);

    scope.querySelectorAll('[data-form-errors]').forEach((errors) => {
      if (errors.dataset.focused) return;
      errors.focus();
      errors.dataset.focused = 'true';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
